import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Activity,
  CheckCircle2,
  RefreshCw,
  AlertTriangle,
  Filter,
  Trash2,
  Radio,
  Building2,
  Moon,
} from 'lucide-react';
import toast from 'react-hot-toast';
import LoadingSpinner from '../common/LoadingSpinner';

const ADMIN_CLOSE_NOTE =
  "[Admin] Sessione chiusa manualmente (allenamento lasciato aperto senza Concludi).";

const SNAPSHOT_INTERVAL_MS = 45000;

const STATO_LABEL = {
  completo: "Completo",
  parziale_chiuso: "Chiuso parziale",
  aperto_vuoto: "Aperto · 0 serie",
  aperto_con_serie: "Aperto · con serie",
};

const STATO_BADGE = {
  completo: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/35 dark:text-emerald-200",
  parziale_chiuso: "bg-amber-100 text-amber-900 dark:bg-amber-900/35 dark:text-amber-100",
  aperto_vuoto: "bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-100",
  aperto_con_serie: "bg-orange-100 text-orange-900 dark:bg-orange-900/35 dark:text-orange-100",
};

const AdminOpenWorkoutsMonitor = () => {
  const { getAuthenticatedApi } = useAuth();
  const api = getAuthenticatedApi();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [onlyToday, setOnlyToday] = useState(false);
  const [closingId, setClosingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [expanded, setExpanded] = useState({});

  const [snap, setSnap] = useState(null);
  const [snapLoading, setSnapLoading] = useState(false);
  const [snapError, setSnapError] = useState(null);
  const [snapAuto, setSnapAuto] = useState(true);

  const [gyms, setGyms] = useState([]);
  const [gymsLoading, setGymsLoading] = useState(true);
  const [gymsError, setGymsError] = useState(null);
  const [savingGymId, setSavingGymId] = useState(null);

  const fetchGyms = useCallback(async () => {
    if (!api) return;
    setGymsLoading(true);
    setGymsError(null);
    try {
      const { data } = await api.get("/gyms.php");
      const list = Array.isArray(data) ? data : [];
      list.sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "it"));
      setGyms(list);
    } catch (err) {
      console.error(err);
      setGymsError(err.response?.data?.error || err.message || "Errore caricamento palestre");
      setGyms([]);
    } finally {
      setGymsLoading(false);
    }
  }, [api]);

  const setGymAutoClose = useCallback(
    async (gymId, enabled) => {
      if (!api) return;
      setSavingGymId(gymId);
      try {
        await api.put(`/gyms.php?id=${gymId}`, {
          auto_close_open_workouts_nightly: enabled ? 1 : 0,
        });
        setGyms((prev) =>
          prev.map((g) =>
            g.id === gymId ? { ...g, auto_close_open_workouts_nightly: enabled ? 1 : 0 } : g
          )
        );
        toast.success(enabled ? "Chiusura automatica attivata." : "Chiusura automatica disattivata.");
      } catch (err) {
        console.error(err);
        toast.error(err.response?.data?.error || "Salvataggio non riuscito");
      } finally {
        setSavingGymId(null);
      }
    },
    [api]
  );

  const fetchSnapshot = useCallback(
    async (opts = {}) => {
      const silent = opts.silent === true;
      if (!api) return;
      if (!silent) {
        setSnapLoading(true);
        setSnapError(null);
      }
      try {
        const { data } = await api.get("/admin_workouts_today_snapshot.php", { params: { limit: 250 } });
        if (!data?.success) {
          throw new Error(data?.error || "Risposta non valida");
        }
        setSnap(data);
        if (silent) {
          setSnapError(null);
        }
      } catch (err) {
        console.error(err);
        if (!silent) {
          setSnapError(err.response?.data?.error || err.message || "Errore riepilogo oggi");
          setSnap(null);
        }
      } finally {
        if (!silent) {
          setSnapLoading(false);
        }
      }
    },
    [api]
  );

  const fetchData = useCallback(async () => {
    if (!api) return;
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 150, only_today: onlyToday ? "1" : "0" };
      const { data } = await api.get("/admin_open_workouts.php", { params });
      if (!data?.success) {
        throw new Error(data?.error || "Risposta non valida");
      }
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || err.message || "Errore nel caricamento");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [api, onlyToday]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchGyms();
  }, [fetchGyms]);

  useEffect(() => {
    fetchSnapshot();
  }, [fetchSnapshot]);

  useEffect(() => {
    if (!snapAuto || !api) return undefined;
    const t = setInterval(() => fetchSnapshot({ silent: true }), SNAPSHOT_INTERVAL_MS);
    return () => clearInterval(t);
  }, [snapAuto, api, fetchSnapshot]);

  const toggleRow = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const forceComplete = useCallback(
    async (allenamentoId, existingNote) => {
      if (!api) return;
      const ok = window.confirm(
        "Chiudere questo allenamento impostando una durata minima (1) e salvando le serie già registrate?\nL'operazione è irreversibile dal punto di vista dello stato 'in corso'."
      );
      if (!ok) return;
      setClosingId(allenamentoId);
      const prev = (existingNote || "").trim();
      const note = prev ? `${prev}\n${ADMIN_CLOSE_NOTE}` : ADMIN_CLOSE_NOTE;
      try {
        await api.put(`/allenamenti.php?id=${allenamentoId}`, {
          durata_totale: 1,
          note,
        });
        toast.success("Allenamento chiuso.");
        await fetchData();
        await fetchSnapshot({ silent: true });
      } catch (err) {
        console.error(err);
        toast.error(err.response?.data?.error || "Chiusura non riuscita");
      } finally {
        setClosingId(null);
      }
    },
    [api, fetchData, fetchSnapshot]
  );

  const deleteAllenamento = useCallback(
    async (row) => {
      if (!api) return;
      const n = row.totali?.serie_registrate ?? 0;
      const msg =
        n === 0
          ? "Eliminare questo allenamento aperto?\n\nNon risultano serie registrate (0): verrà rimosso solo il record della sessione, senza altri dati collegati."
          : `Eliminare questo allenamento aperto?\n\nSono presenti ${n} serie già registrate su questa sessione: verranno eliminate insieme all'allenamento (come da logica server). Operazione irreversibile.`;
      if (!window.confirm(msg)) return;
      setDeletingId(row.allenamento_id);
      try {
        await api.delete(`/allenamenti.php?id=${row.allenamento_id}`);
        toast.success("Allenamento eliminato.");
        await fetchData();
        await fetchSnapshot({ silent: true });
      } catch (err) {
        console.error(err);
        toast.error(err.response?.data?.error || "Eliminazione non riuscita");
      } finally {
        setDeletingId(null);
      }
    },
    [api, fetchData, fetchSnapshot]
  );

  const refreshAll = useCallback(() => {
    fetchData();
    fetchSnapshot();
    fetchGyms();
  }, [fetchData, fetchSnapshot, fetchGyms]);

  const formatClock = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? "—"
      : d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  };

  const formatDt = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    return Number.isNaN(d.getTime())
      ? String(value)
      : d.toLocaleString("it-IT", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900 dark:text-white">
            <Activity className="h-7 w-7 text-amber-500" />
            Allenamenti non conclusi (palestra)
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-gray-600 dark:text-gray-400">
            Clienti con <code className="rounded bg-gray-100 px-1 dark:bg-gray-800">durata_totale</code>{" "}
            ancora vuota: non hanno premuto Concludi. Confronto tra serie previste dalla scheda e serie
            già salvate su questa sessione. Puoi chiudere manualmente la sessione (durata 1) oppure
            eliminarla: in quel caso il server rimuove prima le eventuali{" "}
            <code className="rounded bg-gray-100 px-1 dark:bg-gray-800">serie_completate</code> legate
            e poi {"l'allenamento"}. Le sessioni con{" "}
            <strong>serie registrate = 0</strong> sono quelle vuote, ideali da cancellare; se ci sono
            serie, la conferma te lo ricorda in modo esplicito.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800">
            <Filter className="h-4 w-4" />
            <input
              type="checkbox"
              checked={onlyToday}
              onChange={(e) => setOnlyToday(e.target.checked)}
              className="rounded border-gray-300"
            />
            Solo oggi
          </label>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800">
            <Radio className="h-4 w-4" />
            <input
              type="checkbox"
              checked={snapAuto}
              onChange={(e) => setSnapAuto(e.target.checked)}
              className="rounded border-gray-300"
            />
            Live oggi (45s)
          </label>
          <button
            type="button"
            onClick={() => refreshAll()}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Aggiorna
          </button>
        </div>
      </div>

      <section className="mb-6 rounded-xl border border-amber-200/90 bg-gradient-to-br from-amber-50/90 to-white px-4 py-4 shadow-sm dark:border-amber-900/40 dark:from-amber-950/30 dark:to-gray-900/80">
        <div className="flex flex-wrap items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-100">
            <Moon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="flex flex-wrap items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
              <Building2 className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              Chiusura automatica (cron server, 00:30)
            </h2>
            <p className="mt-1 text-xs leading-relaxed text-gray-600 dark:text-gray-400">
              Abilita per palestra: ogni notte alle <strong>00:30</strong> (orario del server, come gli altri
              cron) vengono chiuse le sessioni ancora senza &quot;Concludi&quot; (
              <code className="rounded bg-white/80 px-1 dark:bg-gray-800">durata_totale</code> vuota), con la
              stessa logica della chiusura manuale da questa pagina (durata minima 1 e nota in coda). Vale per
              i clienti con membership attiva come membro; la palestra di riferimento è la stessa usata in
              questo monitor (prima membership membro ordinata per ID palestra).
            </p>
            {gymsError && (
              <p className="mt-2 text-xs text-red-600 dark:text-red-400">{gymsError}</p>
            )}
            {gymsLoading ? (
              <div className="mt-3 flex justify-center py-4">
                <LoadingSpinner />
              </div>
            ) : gyms.length === 0 ? (
              <p className="mt-3 text-xs text-gray-500">Nessuna palestra trovata.</p>
            ) : (
              <ul className="mt-3 max-h-48 divide-y divide-amber-100 overflow-y-auto rounded-lg border border-amber-100/80 bg-white/70 dark:divide-amber-900/30 dark:border-amber-900/40 dark:bg-gray-950/40">
                {gyms.map((g) => {
                  const on = Number(g.auto_close_open_workouts_nightly) === 1;
                  const busy = savingGymId === g.id;
                  return (
                    <li
                      key={g.id}
                      className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm"
                    >
                      <span className="font-medium text-gray-800 dark:text-gray-100">
                        {g.name || `Palestra #${g.id}`}
                      </span>
                      <label className="inline-flex cursor-pointer items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                        <input
                          type="checkbox"
                          checked={on}
                          disabled={busy}
                          onChange={(e) => setGymAutoClose(g.id, e.target.checked)}
                          className="rounded border-gray-300 text-amber-600 focus:ring-amber-500 disabled:opacity-50"
                        />
                        {busy ? "Salvataggio…" : on ? "Auto-chiusura attiva" : "Auto-chiusura off"}
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
            <p className="mt-2 text-[10px] text-gray-500 dark:text-gray-500">
              Crontab esempio: <code className="rounded bg-black/5 px-1 dark:bg-white/10">30 0 * * * php …/admin_auto_close_open_workouts_cron.php</code>
            </p>
          </div>
        </div>
      </section>

      {/* Riepilogo compatto: sessioni con data = oggi (DB) */}
      <section className="mb-5 rounded-lg border border-slate-200 bg-slate-50/90 px-3 py-2 shadow-sm dark:border-slate-600 dark:bg-slate-900/40">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] leading-tight text-slate-700 dark:text-slate-200">
          <span className="font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Oggi
          </span>
          <span className="text-slate-500 dark:text-slate-400">{snap?.reference_date ?? "—"}</span>
          {snapAuto && (
            <span className="inline-flex items-center gap-1 rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-800 dark:bg-green-900/40 dark:text-green-200">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-60" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-green-600" />
              </span>
              Live
            </span>
          )}
          <span className="text-slate-400 dark:text-slate-500">· agg. {formatClock(snap?.generated_at)}</span>
          {snapLoading && !snap && <span className="text-slate-400">caricamento…</span>}
        </div>
        {snapError && (
          <p className="mt-1 text-[11px] text-red-600 dark:text-red-400">{snapError}</p>
        )}
        {snap?.counts && (
          <>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {[
                ["completo", snap.counts.completo],
                ["parziale_chiuso", snap.counts.parziale_chiuso],
                ["aperto_con_serie", snap.counts.aperto_con_serie],
                ["aperto_vuoto", snap.counts.aperto_vuoto],
              ].map(([key, n]) => (
                <span
                  key={key}
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${STATO_BADGE[key] || ""}`}
                  title={STATO_LABEL[key]}
                >
                  {STATO_LABEL[key]}: {n ?? 0}
                </span>
              ))}
              <span className="inline-flex items-center rounded-full border border-slate-300 px-2 py-0.5 text-[11px] font-medium text-slate-600 dark:border-slate-500 dark:text-slate-300">
                Tot.: {snap.counts.totale ?? 0}
              </span>
            </div>
            <p className="mt-1.5 text-[10px] text-slate-500 dark:text-slate-400">
              Completi = conclusi e serie ≥ previste. Chiuso parziale = conclusi ma mancano serie. Aperti
              0 serie = sessione senza registrazioni. Aperti con serie = in corso con dati salvati.
            </p>
          </>
        )}
        <details className="mt-2 border-t border-slate-200/90 pt-2 dark:border-slate-600/80">
          <summary className="cursor-pointer select-none text-[11px] font-medium text-slate-600 hover:text-slate-800 dark:text-slate-300 dark:hover:text-white">
            Tabella compatta (max-h 9rem)
          </summary>
          <div className="mt-2 max-h-36 overflow-auto rounded border border-slate-200/80 bg-white dark:border-slate-700 dark:bg-slate-950/60">
            <table className="w-full text-left text-[10px]">
              <thead className="sticky top-0 bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                <tr>
                  <th className="px-1.5 py-1">Stato</th>
                  <th className="px-1.5 py-1">Utente</th>
                  <th className="px-1.5 py-1">Scheda</th>
                  <th className="px-1.5 py-1">Serie</th>
                  <th className="px-1.5 py-1">ID</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-800 dark:divide-slate-800 dark:text-slate-200">
                {(snap?.rows || []).map((r) => (
                  <tr key={r.allenamento_id}>
                    <td className="whitespace-nowrap px-1.5 py-0.5">
                      <span className={`rounded px-1 py-0.5 ${STATO_BADGE[r.stato] || ""}`}>
                        {STATO_LABEL[r.stato] || r.stato}
                      </span>
                    </td>
                    <td className="max-w-[7rem] truncate px-1.5 py-0.5" title={r.email || ""}>
                      {r.user_name || "—"}
                    </td>
                    <td className="max-w-[6rem] truncate px-1.5 py-0.5" title={r.scheda_nome || ""}>
                      {r.scheda_nome || "—"}
                    </td>
                    <td className="whitespace-nowrap px-1.5 py-0.5">
                      {r.serie_registrate}/{r.serie_previste}
                    </td>
                    <td className="whitespace-nowrap px-1.5 py-0.5 text-slate-500">{r.allenamento_id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {snap?.rows?.length === 0 && !snapLoading && (
              <p className="p-2 text-center text-[10px] text-slate-500">Nessuna sessione oggi.</p>
            )}
          </div>
        </details>
      </section>

      {error && (
        <div className="mb-6 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      ) : items.length === 0 ? (
        <p className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-400">
          Nessun allenamento aperto trovato con i filtri attuali.
        </p>
      ) : (
        <div className="space-y-4">
          {items.map((row) => {
            const open = expanded[row.allenamento_id];
            const totals = row.totali || {};
            const mismatch =
              totals.serie_registrate < totals.serie_previste || totals.serie_previste === 0;
            const reg = totals.serie_registrate ?? 0;
            const noSeries = reg === 0;
            const busy = closingId === row.allenamento_id || deletingId === row.allenamento_id;
            return (
              <div
                key={row.allenamento_id}
                className={`overflow-hidden rounded-xl border bg-white shadow-sm dark:bg-gray-900 ${
                  noSeries
                    ? "border-emerald-200 dark:border-emerald-900/50"
                    : "border-gray-200 dark:border-gray-700"
                }`}
              >
                <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {row.user_name || row.username || `Utente #${row.user_id}`}
                      </span>
                      {row.is_today && (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
                          Oggi
                        </span>
                      )}
                      {noSeries ? (
                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100">
                          Serie registrate: 0 — sessione vuota
                        </span>
                      ) : (
                        <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-900 dark:bg-orange-900/40 dark:text-orange-100">
                          Con {reg} serie salvate (elimina = rimuove anche le serie)
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {row.email} · {row.gym_name || "Palestra —"}
                    </p>
                    <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-medium">{row.scheda_nome}</span>
                      <span className="text-gray-400"> · </span>
                      {formatDt(row.data_allenamento)}
                      <span className="text-gray-400"> · </span>
                      ID allenamento {row.allenamento_id}
                    </p>
                    <p className="mt-1 text-sm">
                      <span className={mismatch ? "text-amber-700 dark:text-amber-300" : "text-green-700 dark:text-green-300"}>
                        <strong>Totale serie registrate:</strong> {reg} ·{" "}
                        <strong>previste (scheda):</strong> {totals.serie_previste ?? 0}
                      </span>
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => toggleRow(row.allenamento_id)}
                      disabled={busy}
                      className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
                    >
                      {open ? "Nascondi dettaglio" : "Dettaglio esercizi"}
                    </button>
                    <button
                      type="button"
                      onClick={() => forceComplete(row.allenamento_id, row.allenamento_note)}
                      disabled={busy}
                      className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                      <CheckCircle2 className="h-4 w-4" />
                      {closingId === row.allenamento_id ? "Chiusura…" : "Chiudi allenamento"}
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteAllenamento(row)}
                      disabled={busy}
                      className="inline-flex items-center gap-2 rounded-lg border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:bg-gray-900 dark:text-red-300 dark:hover:bg-red-950/40"
                    >
                      <Trash2 className="h-4 w-4" />
                      {deletingId === row.allenamento_id ? "Eliminazione…" : "Elimina allenamento"}
                    </button>
                  </div>
                </div>

                {open && (
                  <div className="border-t border-gray-100 bg-gray-50/80 p-4 dark:border-gray-800 dark:bg-gray-950/50">
                    <table className="w-full text-left text-sm">
                      <thead>
                        <tr className="text-xs uppercase text-gray-500 dark:text-gray-400">
                          <th className="pb-2 pr-2">#</th>
                          <th className="pb-2 pr-2">Esercizio</th>
                          <th className="pb-2 pr-2">Previste</th>
                          <th className="pb-2 pr-2">Registrate</th>
                          <th className="pb-2">Stato</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {(row.exercises || []).map((ex) => (
                          <tr key={ex.scheda_esercizio_id} className="text-gray-800 dark:text-gray-200">
                            <td className="py-2 pr-2">{ex.ordine}</td>
                            <td className="py-2 pr-2">{ex.esercizio_nome}</td>
                            <td className="py-2 pr-2">{ex.serie_previste}</td>
                            <td className="py-2 pr-2">{ex.serie_registrate}</td>
                            <td className="py-2">
                              {ex.allineato ? (
                                <span className="text-green-600 dark:text-green-400">OK</span>
                              ) : (
                                <span className="text-amber-700 dark:text-amber-300">In sospeso</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminOpenWorkoutsMonitor;
